import React, { useState, useEffect } from 'react';
import { Tag, Loader2, FileText, Save, XCircle, AlertTriangle, ShieldCheck } from 'lucide-react';

export default function LocationForm({ location, onSave, onCancel, isNew, submitting: propSubmitting }) {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isActive: true,
    });
    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (location) {
            setFormData({
                id: location.id,
                name: location.name || '',
                description: location.description || '',
                isActive: location.isActive ?? true,
            });
        } else {
            setFormData({
                name: '',
                description: '',
                isActive: true,
            });
        }
        setFormError(null);
    }, [location, isNew]);

    const currentSubmittingState = propSubmitting || submitting;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        const trimmedName = (formData.name || '').trim();
        if (!trimmedName) {
            setFormError("Lokasyon adı boş bırakılamaz.");
            return;
        }

        setSubmitting(true);
        try {
            await onSave({
                ...formData,
                name: trimmedName,
                description: (formData.description || '').trim()
            });
        } catch (err) {
            setFormError(err.message || "Kaydetme işlemi sırasında bir hata oluştu.");
        } finally {
            setSubmitting(false);
        }
    };

    const submitButtonText = isNew ? "Lokasyon Ekle" : "Değişiklikleri Kaydet";
    const activeEmpCount = location?.activeEmployeeCount || 0;
    const authUserCount = location?.authorizedUserCount || 0;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                    <span>{formError}</span>
                </div>
            )}

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
                    <Tag className="w-4 h-4 mr-1.5 text-indigo-500" />
                    Lokasyon / Şube / Tesis Adı <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    placeholder="Örn: Genel Merkez, Gebze Fabrika, Maslak Ofis"
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    disabled={currentSubmittingState}
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
                    <FileText className="w-4 h-4 mr-1.5 text-indigo-500" />
                    Açıklama / Adres Notu
                </label>
                <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={500}
                    rows="3"
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    disabled={currentSubmittingState}
                    placeholder="Lokasyon adresi veya tesis açıklaması (isteğe bağlı)..."
                />
            </div>

            <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                    <label htmlFor="isActive" className="text-sm font-semibold text-gray-800 dark:text-gray-200 cursor-pointer">
                        Lokasyon Durumu (Aktif)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Pasif lokasyonlar yeni personel atamalarında ve Excel şablonlarında listelenmez.
                    </p>
                </div>
                <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                    disabled={currentSubmittingState}
                />
            </div>

            {!isNew && !formData.isActive && activeEmpCount > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 p-3 rounded-xl text-xs flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                    <span>
                        Bu lokasyonda halen <strong>{activeEmpCount} aktif personel</strong> kayıtlıdır. Lokasyonu pasife aldığınızda mevcut personeller etkilenmez ancak yeni personel atamalarında seçilemez.
                    </span>
                </div>
            )}

            {!isNew && authUserCount > 0 && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/60 text-indigo-800 dark:text-indigo-200 p-3 rounded-xl text-xs flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-indigo-500" />
                    <span>
                        Bu lokasyon <strong>{authUserCount} yönetici/kullanıcı</strong> tarafından veri görme kapsamı (DataScope) filtresi olarak kullanılmaktadır.
                    </span>
                </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 rounded-xl transition"
                    disabled={currentSubmittingState}
                >
                    <XCircle className="w-4 h-4 mr-1.5" />
                    İptal
                </button>
                <button
                    type="submit"
                    className="flex items-center px-5 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white rounded-xl shadow-sm transition"
                    disabled={currentSubmittingState}
                >
                    {currentSubmittingState ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            Kaydediliyor...
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-1.5" />
                            {submitButtonText}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}
